import { Component, Input, OnChanges, OnDestroy, SimpleChanges, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, interval } from 'rxjs';
import { switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import {
  CameraEventFetchDto,
  CameraEventFetchStatus,
  CameraEventReplayDecision,
  CameraEventReplayItemDto,
  CameraEventReplayPreviewDto,
  CameraEventReplayStatus
} from '../../../../../../../api/gen';
import { CameraEventService } from '../../../../../../../api/gen/api/cameraEvent.service';

/** Progress refresh while a replay runs; the worker sends one entry every CameraEventPolicy.ReplayDelaySeconds (3 s). */
const PROGRESS_POLL_MS = 5_000;

interface ReplayResultSummary {
  replayed: number;
  failed: number;
  queued: number;
  outcomes: { label: string; count: number }[];
  skipped: { label: string; count: number }[];
  startedAt?: string;
  finishedAt?: string;
}

function countBy<T>(items: T[], key: (item: T) => string): { label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const item of items) counts.set(key(item), (counts.get(key(item)) ?? 0) + 1);
  return [...counts].map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
}

@Component({
  selector: 'app-camera-event-replay',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSnackBarModule
  ],
  templateUrl: './camera-event-replay.component.html',
  styleUrl: './camera-event-replay.component.scss'
})
export class CameraEventReplayComponent implements OnChanges, OnDestroy {
  @Input() cameraId?: string;
  @Input() fetch: CameraEventFetchDto | null = null;

  preview = signal<CameraEventReplayPreviewDto | null>(null);
  isLoading = signal(false);
  isSubmitting = signal(false);
  confirming = signal(false);
  showAll = signal(false);

  visibleItems = computed(() => {
    const items = this.preview()?.items ?? [];
    return this.showAll()
      ? items
      : items.filter(i => i.decision === CameraEventReplayDecision.Replay || i.replayStatus !== CameraEventReplayStatus.NotReplayed);
  });

  /** End-of-run result, built from the records already in the preview — no extra request. */
  result = computed<ReplayResultSummary | null>(() => {
    const p = this.preview();
    if (!p?.items) return null;
    const done = p.items.filter(i =>
      i.replayStatus === CameraEventReplayStatus.Replayed || i.replayStatus === CameraEventReplayStatus.Failed);
    if (done.length === 0) return null;

    const failed = done.filter(i => i.replayStatus === CameraEventReplayStatus.Failed);
    const skipped = p.items.filter(i =>
      i.replayStatus === CameraEventReplayStatus.NotReplayed && i.decision !== CameraEventReplayDecision.Replay);
    const times = done.map(i => i.replayedAt).filter((t): t is string => !!t).sort();

    return {
      replayed: done.length - failed.length,
      failed: failed.length,
      queued: p.queuedCount ?? 0,
      outcomes: countBy(done.filter(i => i.replayStatus === CameraEventReplayStatus.Replayed), i => i.replayResult || 'Unknown'),
      skipped: countBy(skipped, i => i.decision ?? 'Unknown'),
      startedAt: times[0],
      finishedAt: (p.queuedCount ?? 0) === 0 ? times[times.length - 1] : undefined
    };
  });

  readonly displayedColumns =['plateNumber', 'detectionTime', 'decision', 'replayStatus', 'detail'];
  readonly Decision = CameraEventReplayDecision;
  readonly ReplayStatus = CameraEventReplayStatus;

  private cameraEventService = inject(CameraEventService);
  private snackBar = inject(MatSnackBar);

  private stopPolling$ = new Subject<void>();

  get canReplay(): boolean {
    return this.fetch?.status === CameraEventFetchStatus.Completed && !!this.cameraId;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fetch']) {
      // A preview runs a backend detection lookup, so it is loaded on request, not on every selection.
      this.stopPolling$.next();
      this.preview.set(null);
      this.confirming.set(false);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
  }

  loadPreview(): void {
    if (!this.canReplay) return;
    this.isLoading.set(true);
    this.cameraEventService.cameraEventsFetchIdReplayGet(this.fetch!.id!, this.cameraId).subscribe({
      next: (result) => {
        this.preview.set(result);
        this.isLoading.set(false);
        if ((result.queuedCount ?? 0) > 0) this.pollProgress();
      },
      error: () => {
        this.snackBar.open('Failed to check for missed entries.', 'Close', { duration: 4000 });
        this.isLoading.set(false);
      }
    });
  }

  replay(): void {
    if (!this.canReplay) return;
    this.confirming.set(false);
    this.isSubmitting.set(true);
    this.cameraEventService.cameraEventsFetchIdReplayPost(this.fetch!.id!, { cameraId: this.cameraId! }).subscribe({
      next: (result) => {
        this.preview.set(result);
        this.isSubmitting.set(false);
        this.pollProgress();
      },
      error: () => {
        this.snackBar.open('Failed to start the replay.', 'Close', { duration: 4000 });
        this.isSubmitting.set(false);
      }
    });
  }

  private pollProgress(): void {
    this.stopPolling$.next();
    interval(PROGRESS_POLL_MS).pipe(
      switchMap(() => this.cameraEventService.cameraEventsFetchIdReplayGet(this.fetch!.id!, this.cameraId)),
      takeWhile(result => (result.queuedCount ?? 0) > 0, true),
      takeUntil(this.stopPolling$)
    ).subscribe({
      next: (result) => {
        const wasRunning = this.isRunning;
        this.preview.set(result);
        if (wasRunning && !this.isRunning) {
          const r = this.result();
          this.snackBar.open(`Replay finished: ${r?.replayed ?? 0} replayed, ${r?.failed ?? 0} failed.`, 'Close', { duration: 6000 });
        }
      },
      error: () => this.snackBar.open('Lost track of replay progress. Check again later.', 'Close', { duration: 4000 })
    });
  }

  get isRunning(): boolean {
    return (this.preview()?.queuedCount ?? 0) > 0;
  }

  getDecisionColor(item: CameraEventReplayItemDto): string {
    switch (item.decision) {
      case CameraEventReplayDecision.Replay: return 'primary';
      case CameraEventReplayDecision.AlreadyReplayed: return 'accent';
      default: return '';
    }
  }

  getStatusIcon(status?: CameraEventReplayStatus): string {
    switch (status) {
      case CameraEventReplayStatus.Queued: return 'schedule';
      case CameraEventReplayStatus.Replayed: return 'check_circle';
      case CameraEventReplayStatus.Failed: return 'error';
      default: return 'remove';
    }
  }

  trackItem(_: number, item: CameraEventReplayItemDto): string | undefined {
    return item.cameraEventRecordId;
  }
}
